import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Container,
  Button,
  Chip,
  Divider,
  Link,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { motion } from "framer-motion";
import { ArrowBack, GitHub, Public, DataObject } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AddHomeWorkIcon from "@mui/icons-material/AddHomeWork";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import ShowChartIcon from "@mui/icons-material/ShowChart";

const projectsData = [
  {
    id: 1,
    title: "Alumni Path tracker",
    description: "",
    longDescription: "",
    tags: ["React", "Node.js", "SQL"],
    features: [
      "Student/Alumni verification by admin",
      "Mentorship sessions",
      "Event and job creation",
      "Iequest recommendation letters based on projects",
      "Report incidents to admin",
      "Skill, company, and role-based search preferences",
    ],
    githubUrl: "https://github.com/shimul47/GradLink",
    liveUrl: "/upcoming",
    technologies: [
      { name: "Frontend", purpose: "React" },
      { name: "Backend", purpose: "Node.js" },
      { name: "Database", purpose: "SQL" },
      { name: "Deployment", purpose: "Upcoming" },
      { name: "AI Integration", purpose: "Upcoming" },
    ],
    imageUrl: "../media/images/gradlink.png",
    icon: <DataObject fontSize="large" />,
  },
  {
    id: 2,
    title: "Wanderlust",
    description:
      "A scalable cloud solution built with modern infrastructure-as-code practices",
    longDescription:
      "A full-stack clone of the Airbnb platform featuring user authentication, property listings with location support, and structured using the MVC framework.",
    tags: [
      "EJS",
      "Node.js",
      "Express.js",
      "MongoDB",
      "MapBox",
      "Authentication",
      "Bootstrap",
    ],
    features: [
      "User Authentication (Sign Up, Login)",
      "Listing Properties",
      "Booking System",
      "Location",
      "Payment Integration (Upcoming)",
      "Responsive UI (Upcoming)",
    ],
    githubUrl: "https://github.com/shimul47/Project_Airbnb",
    liveUrl: "https://project-airbnb-ob1g.onrender.com/listings",
    technologies: [
      { name: "Frontend", purpose: "EJS Script,Bootstrap" },
      { name: "Backend", purpose: "Node.js" },
      { name: "Cloud", purpose: "MongoDB" },
      { name: "Deployment", purpose: "Render" },
    ],
    imageUrl: "../media/images/wanderlust.png",
    icon: <AddHomeWorkIcon fontSize="large" />,
  },
  {
    id: 3,
    title: "Stock Market Lite",
    description:
      "Stock Market Lite is a full-stack web app that provides real-time stock data with user authentication and personalized dashboards.",
    longDescription: (
      <div>
        <p>
          Stock Market Lite is a lightweight and intuitive stock market web
          application designed to provide users with real-time financial data.
          The platform allows authenticated users to track live stock prices,
          access company information, and manage their own dashboard seamlessly.
          Built with a modern tech stack, this project demonstrates the power of
          full-stack web development with live API integration and user
          authentication.
        </p>
        <p style={{ color: "red" }}>
          <b>
            <i>
              N.B: The backend is hosted on Render (free tier), so responses may
              be delayed or occasionally crash due to server sleep.
            </i>
          </b>
        </p>
      </div>
    ),
    tags: ["Node.js", "React", "Bootstrap", "MongoDB", "Firebase"],
    features: [
      "Firebase authentication with Google login",
      "Personalized user dashboard",
      "Live stock market data via API",
      "Interactive charts and graphs for visualizing stock trends and performance.",
    ],
    githubUrl: "/ggwp",
    liveUrl: "https://stock-market-lite101.netlify.app/",
    technologies: [
      { name: "Node.js", purpose: "Backend services" },
      { name: "React", purpose: "Frontend" },
      { name: "Firebase", purpose: "Authentication" },
      { name: "Database", purpose: "MongoDB" },
      { name: "Deployment", purpose: "Backend : Render, Frontend : Netlify" },
    ],
    imageUrl: "../media/images/stockmarket.png",
    icon: <ShowChartIcon fontSize="large" />,
  },
  {
    id: 4,
    title: "Weather API",
    description:
      "A React app that shows real-time weather info with city search and weather-based images.",
    longDescription:
      "Weather App is a simple yet effective React-based web application that allows users to check real-time weather information by entering any city name. It fetches live data from a public weather API and displays temperature, weather conditions, and other relevant details in a clean and responsive interface.",
    tags: ["React", "Weather API", "Tailwind"],
    features: [
      "Real-time weather data via API",
      "City-based search functionality",
      "Clean and responsive UI with React",
      "Error handling for invalid or misspelled cities",
      "Displays weather-specific images and icons for better visual understanding.",
    ],
    githubUrl: "/ggwp",
    liveUrl: "https://zingy-lily-22c598.netlify.app/",
    technologies: [
      { name: "React", purpose: "Frontend" },
      { name: "Open Weather Map", purpose: "Live API" },
      { name: "Tailwind.css", purpose: "Design" },
    ],
    imageUrl: "../media/images/weather.png",
    icon: <ThunderstormIcon fontSize="large" />,
  },
];

const ProjectDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const project = projectsData.find((proj) => proj.id === parseInt(id));
  const from = location.state?.from;

  useEffect(() => {
    return () => {
      if (from === "projects") {
        sessionStorage.setItem("scrollTarget", "projects");
      }
    };
  }, []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!project) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography variant="h4">Project not found</Typography>
        <Button component={RouterLink} to="/" sx={{ mt: 3 }}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            sx={{ mb: 4, mt: 2 }}
            variant="text"
          >
            Back to Projects
          </Button>

          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                color: theme.palette.text.primary,
                mt: 2,
              }}
            >
              <Box sx={{ color: "primary.main" }}>{project.icon}</Box>
              {project.title}
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
              {project.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    fontFamily: '"Roboto Mono", monospace',
                    fontSize: "0.7rem",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(56, 189, 248, 0.2)"
                        : "rgba(37, 99, 235, 0.1)",
                    color: theme.palette.text.primary,
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 3, mb: 4 }}>
              {project.githubUrl && (
                <Button
                  variant="contained"
                  startIcon={<GitHub />}
                  component={Link}
                  href={project.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                  }}
                >
                  View Code
                </Button>
              )}
              {project.liveUrl && (
                <Button
                  variant="outlined"
                  startIcon={<Public />}
                  component={Link}
                  href={project.liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 600,
                    px: 3,
                    py: 1,
                    borderWidth: "2px",
                    "&:hover": {
                      borderWidth: "2px",
                    },
                  }}
                >
                  Live Demo
                </Button>
              )}
            </Box>
          </Box>

          <Grid container spacing={6}>
            <Grid item xs={12} md={8}>
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, mb: 3 }}
                >
                  Project Overview
                </Typography>
                <Typography paragraph sx={{ mb: 4, lineHeight: 1.8 }}>
                  {project.longDescription}
                </Typography>

                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{ fontWeight: 600, mt: 6, mb: 3 }}
                >
                  Key Features
                </Typography>
                <List sx={{ mb: 4 }}>
                  {project.features.map((feature, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: "list-item",
                        pl: 0,
                        ml: 2,
                        py: 0.5,
                        "&::marker": {
                          color: "primary.main",
                        },
                      }}
                    >
                      <Typography>{feature}</Typography>
                    </ListItem>
                  ))}
                </List>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 600 }}
                  >
                    Technologies Used
                  </Typography>
                  <List dense>
                    {project.technologies.map((tech, index) => (
                      <ListItem key={index} sx={{ py: 1 }}>
                        <ListItemText
                          primary={tech.name}
                          secondary={tech.purpose}
                        />
                      </ListItem>
                    ))}
                  </List>

                  {project.imageUrl && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <img
                          src={project.imageUrl}
                          alt={project.title}
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                      </Box>
                    </>
                  )}
                </Paper>
              </motion.div>
            </Grid>
          </Grid>

          <Box sx={{ mt: 6, textAlign: "center" }}>
            <Button
              component={RouterLink}
              to="/"
              variant="outlined"
              startIcon={<ArrowBack />}
              size="large"
              sx={{
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderWidth: "2px",
                "&:hover": {
                  borderWidth: "2px",
                },
              }}
            >
              Back to All Projects
            </Button>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ProjectDetails;
